import type React from "react";
import { useMemo } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { capitalizeFirstLetterFilter } from "app/filters/ngFilters";
import { LLMSetupSectionHeader } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/LLMSetupSectionHeader";
import { SuggestedTopicChip } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/LLMSetupTopicsSection/SuggestedTopicChip";
import { TopicsEmptyState } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/LLMSetupTopicsSection/TopicsEmptyState";

import { TopicsLoadingSpinner } from "./TopicsLoadingSpinner";
import { ChipsRow, GradientWrapper } from "./TopicsSection.styled";

const MAX_DISPLAYED_TOPICS = 8;

interface ILLMSetupTopicsProps {
    suggestedTopics: string[];
    canTopicsBeAdded: boolean;
    isTopicLoading: boolean;
    onSuggestionAccepted: (topic: string) => void;
}

export const SuggestedTopicsSection: React.FC<ILLMSetupTopicsProps> = ({
    suggestedTopics,
    canTopicsBeAdded,
    isTopicLoading,
    onSuggestionAccepted,
}) => {
    const displayedTopics = useMemo<string[]>(() => suggestedTopics.slice(0, MAX_DISPLAYED_TOPICS), [suggestedTopics]);

    return (
        <GradientWrapper
            flexDirection="column"
            gap="8px"
        >
            <LLMSetupSectionHeader
                title={
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[400]}
                        data-automation="llm-setup-topic-suggestions-title"
                    >
                        {translate("llm.setup.topic_suggestions.title")}
                    </Text>
                }
            />
            {isTopicLoading ? (
                <TopicsLoadingSpinner />
            ) : suggestedTopics?.length ? (
                <ChipsRow
                    data-automation="llm-setup-topic-suggestions-stack"
                    flexWrap="wrap"
                    gap="8px"
                    alignContent="flex-start"
                >
                    {displayedTopics.map((topic, index) => (
                        <SuggestedTopicChip
                            key={topic}
                            label={capitalizeFirstLetterFilter()(topic)}
                            onClick={() => onSuggestionAccepted(topic)}
                            disabled={!canTopicsBeAdded}
                            data-automation={`llm-setup-topic-suggestion-item-${index}`}
                        />
                    ))}
                </ChipsRow>
            ) : (
                <TopicsEmptyState />
            )}
        </GradientWrapper>
    );
};
